import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { LineModifier, Product, SaleTender } from "@shared/events";
import { uuidv7 } from "@shared/ids";
import { formatMinor } from "@shared/money";
import {
  addToCart,
  cartCount,
  cartTotalMinor,
  decrementLine,
  draftToCustomer,
  draftToLines,
  draftToTip,
  effectiveTipMinor,
  incrementLine,
  removeLine,
  toDraft,
  toLineItems,
  unitsByProduct,
  type CartCustomer,
  type CartLine,
} from "./cart";
import { CartPanel } from "./CartPanel";
import { SellSummaryDisplay, useSellSummaryReporter } from "./summary";
import { CustomerPicker } from "./CustomerPicker";
import { ModifierSheet } from "./ModifierSheet";
import { ProductGrid, remainingStock, sortedSellableProducts } from "./ProductGrid";
import { TenderScreen } from "./TenderScreen";
import { TipSheet } from "./TipSheet";
import { cartDraftIo, useSellRegister, type SellRegister } from "./registerAccess";
import "./sell.css";

export { SellRegisterProvider, SellRegisterContext, type SellRegister } from "./registerAccess";

/** Undo window for the last sale (F-407). */
export const UNDO_WINDOW_MS = 60_000;
/** How long a "sold past zero" warning stays up. */
const WARN_MS = 4_000;

interface LastSale {
  transactionId: string;
  /** What the customer paid: goods plus tip. */
  collectedMinor: number;
  at: number;
}

/**
 * The register (F-401–F-409). Everything a vendor touches 200 times a day: grid, cart, tender, undo.
 * Adds are optimistic and local; the ledger event is dispatched through the store, which persists
 * before it resolves. Nothing here waits on the network.
 */
export function SellScreen(props: { register?: SellRegister } = {}) {
  const reg = useSellRegister(props.register);
  const { state } = reg;
  const settings = reg.settings ?? state.settings;
  const currency = settings.currency;
  const registerId = reg.registerId ?? state.registerId;

  const [lines, setLines] = useState<CartLine[]>([]);
  const [modifierProduct, setModifierProduct] = useState<Product | null>(null);
  const [tendering, setTendering] = useState(false);
  const [warning, setWarning] = useState<string | null>(null);
  const [lastSale, setLastSale] = useState<LastSale | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [customer, setCustomer] = useState<CartCustomer | null>(null);
  const [pickingCustomer, setPickingCustomer] = useState(false);
  const [tipMinor, setTipMinor] = useState(0);
  const [tipping, setTipping] = useState(false);

  // Keyed on the functions it wraps, not on `reg` (D-065): the register object is rebuilt on every store
  // update, and a draft save notifies the store, so keyed on `reg` the save effect below re-ran forever.
  const draft = useMemo(() => cartDraftIo(reg), [reg.saveCartDraft, reg.loadCartDraft, reg.store]); // eslint-disable-line react-hooks/exhaustive-deps
  /** The register whose saved draft has been read back. Until then nothing is saved over it (D-065). */
  const [draftLoadedFor, setDraftLoadedFor] = useState<string | null>(null);
  const warnTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ---- Cart draft survives a refresh or app kill (F-409) ----
  // Marked loaded only once the read finishes (D-065): a load cut short by the effect's cleanup (React's
  // StrictMode re-run, or a registerId change) is simply started again, instead of being lost for good.
  useEffect(() => {
    if (!registerId || draftLoadedFor === registerId) return;
    let live = true;
    void draft.load(registerId).then((raw) => {
      if (!live) return;
      const restored = draftToLines(raw);
      if (restored.length > 0) {
        setLines((current) => (current.length === 0 ? restored : current));
        // D-063: the tip is part of the cart too, and only means something with the lines it came with.
        const restoredTip = draftToTip(raw);
        if (restoredTip > 0) setTipMinor((current) => (current > 0 ? current : restoredTip));
      }
      // D-049: the picked customer is part of the cart and comes back with it (F-409).
      const restoredCustomer = draftToCustomer(raw);
      if (restoredCustomer) setCustomer((current) => current ?? restoredCustomer);
      setDraftLoadedFor(registerId);
    });
    return () => {
      live = false;
    };
  }, [registerId, draft, draftLoadedFor]);

  useEffect(() => {
    if (!registerId || draftLoadedFor !== registerId) return;
    draft.save(registerId, toDraft(lines, customer, tipMinor));
  }, [lines, customer, tipMinor, registerId, draft, draftLoadedFor]);

  // A tip belongs to the goods it came with (D-063): an emptied cart drops it, so it can't ride along on
  // the next customer's sale.
  useEffect(() => {
    if (lines.length === 0) setTipMinor(0);
  }, [lines.length]);

  // ---- Undo countdown (F-407) ----
  useEffect(() => {
    if (!lastSale) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(id);
  }, [lastSale]);

  useEffect(() => () => { if (warnTimer.current) clearTimeout(warnTimer.current); }, []);

  const undoSecondsLeft = lastSale ? Math.ceil((lastSale.at + UNDO_WINDOW_MS - now) / 1000) : 0;
  const undoAvailable = lastSale !== null && undoSecondsLeft > 0;
  useEffect(() => {
    if (lastSale && undoSecondsLeft <= 0) setLastSale(null);
  }, [lastSale, undoSecondsLeft]);

  const products = useMemo(() => sortedSellableProducts(state.products), [state.products]);
  const cartUnits = useMemo(() => unitsByProduct(lines), [lines]);
  const totalMinor = cartTotalMinor(lines);
  const tip = effectiveTipMinor(tipMinor, totalMinor, lines.length);
  /** What to charge: the goods plus any tip (D-063). */
  const dueMinor = totalMinor + tip;
  const itemCount = cartCount(lines);

  // Publish the running total to the app header (top-right), where it shows only while selling (D-051).
  // Standalone (no Shell above us) the reporter is null and we render the summary inline instead — see below.
  const reportSummary = useSellSummaryReporter();
  useEffect(() => {
    reportSummary?.({ count: itemCount, totalMinor: dueMinor });
  }, [reportSummary, itemCount, dueMinor]);
  useEffect(() => () => reportSummary?.(null), [reportSummary]);

  const warn = useCallback((message: string) => {
    setWarning(message);
    if (warnTimer.current) clearTimeout(warnTimer.current);
    warnTimer.current = setTimeout(() => setWarning(null), WARN_MS);
  }, []);

  const addUnits = useCallback(
    (product: Product, modifiers: LineModifier[]) => {
      // A stale count must never block a real sale (F-306): warn, then sell.
      const remaining = remainingStock(product, state.stock, cartUnits);
      if (remaining !== null && remaining <= 0) warn(`${product.label} is sold out — selling anyway`);
      setLines((current) => addToCart(current, product, modifiers));
    },
    [state.stock, cartUnits, warn],
  );

  const pick = useCallback(
    (product: Product) => {
      navigator.vibrate?.(10);
      if (product.modifierGroups.length > 0) {
        setModifierProduct(product);
        return;
      }
      addUnits(product, []);
    },
    [addUnits],
  );

  // F-408: Clear abandons the whole cart being built — the picked customer included (D-049), otherwise a
  // pick made for a walk-away would silently attach to the next stranger's sale.
  const clearCart = useCallback(() => {
    setLines([]);
    setCustomer(null);
    setTipMinor(0);
  }, []);

  const completeSale = useCallback(
    (tender: SaleTender) => {
      if (lines.length === 0) return;
      const items = toLineItems(lines);
      const total = cartTotalMinor(lines);
      const saleTip = effectiveTipMinor(tipMinor, total, lines.length);
      const transactionId = uuidv7();
      reg.dispatch("sale.completed", {
        transactionId,
        dayId: state.day?.id ?? null,
        items,
        subtotalMinor: total,
        totalMinor: total,
        tipMinor: saleTip,
        tender,
        customerId: customer?.id ?? null,
      });
      navigator.vibrate?.([12, 40, 12]);
      setTendering(false);
      setLines([]);
      // Don't carry a customer or a tip over to the next sale (F-901–905, D-063).
      setCustomer(null);
      setTipMinor(0);
      setLastSale({ transactionId, collectedMinor: total + saleTip, at: Date.now() });
    },
    [lines, reg, state.day, customer, tipMinor],
  );

  const undoLastSale = useCallback(() => {
    if (!lastSale) return;
    reg.dispatch("sale.voided", { transactionId: lastSale.transactionId });
    setLastSale(null);
  }, [lastSale, reg]);

  return (
    <section className="sell" aria-label="Sell">
      {reportSummary ? null : (
        <div className="sell__summary-standalone">
          <SellSummaryDisplay summary={{ count: itemCount, totalMinor: dueMinor }} currency={currency} />
        </div>
      )}
      <div className="sell__main">
        {warning ? (
          <p className="sell__warn" role="status" style={{ margin: 8 }}>
            {warning}
          </p>
        ) : null}

        <ProductGrid
          products={products}
          stock={state.stock}
          cartUnits={cartUnits}
          currency={currency}
          onPick={pick}
        />

        {undoAvailable && lastSale ? (
          <div className="sell__undo" role="status">
            <span className="sell__undo-text">
              Sold {formatMinor(lastSale.collectedMinor, currency)} · undo {undoSecondsLeft}s
            </span>
            <button type="button" className="sell__undo-btn" onClick={undoLastSale}>
              Undo sale
            </button>
          </div>
        ) : null}
      </div>

      <CartPanel
        lines={lines}
        currency={currency}
        onIncrement={(key) => setLines((c) => incrementLine(c, key))}
        onDecrement={(key) => setLines((c) => decrementLine(c, key))}
        onRemove={(key) => setLines((c) => removeLine(c, key))}
        onClear={clearCart}
        onCharge={() => setTendering(true)}
        tipMinor={tip}
        onTip={() => setTipping(true)}
        customerName={customer?.name ?? null}
        onPickCustomer={() => setPickingCustomer(true)}
      />

      {modifierProduct ? (
        <ModifierSheet
          product={modifierProduct}
          currency={currency}
          onCancel={() => setModifierProduct(null)}
          onAdd={(modifiers) => {
            addUnits(modifierProduct, modifiers);
            setModifierProduct(null);
          }}
        />
      ) : null}

      {tipping ? (
        <TipSheet
          initialMinor={tip}
          goodsTotalMinor={totalMinor}
          currency={currency}
          onSave={(next) => {
            setTipMinor(next);
            setTipping(false);
          }}
          onCancel={() => setTipping(false)}
        />
      ) : null}

      {tendering ? (
        <TenderScreen
          totalMinor={dueMinor}
          tipMinor={tip}
          currency={currency}
          handles={settings.payHandles}
          registerName={settings.name}
          onComplete={completeSale}
          onCancel={() => setTendering(false)}
        />
      ) : null}

      {pickingCustomer ? (
        <CustomerPicker
          registerId={registerId ?? null}
          onPick={(id, picked) => setCustomer(id && picked ? { id, name: `${picked.firstName} ${picked.lastName}` } : null)}
          onClose={() => setPickingCustomer(false)}
        />
      ) : null}
    </section>
  );
}

export default SellScreen;
